import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n'
import { api, ApiError } from '../lib/api'
import { dialogTitle, isMine, messageUrl, myName, nameOf, setMyName, shareMove, shareTextFor, sysText } from '../lib/dialogs'
import { NameSheet, RenameSheet } from '../components/Sheets'
import { useSession } from '../store/session'
import type { Dialog, Message } from '../lib/types'

export function Room() {
  const { t, locale } = useI18n()
  const s = useSession()
  const d = s.activeDialog
  const [text, setText] = useState('')
  const [guessFor, setGuessFor] = useState<Message | null>(null)
  const [busy, setBusy] = useState(false)
  const [askName, setAskName] = useState(false)
  const [rename, setRename] = useState(false)
  const [lastSent, setLastSent] = useState<Message | null>(null)
  const [copied, setCopied] = useState(false)
  const [err, setErr] = useState('')
  const feedEnd = useRef<HTMLDivElement>(null)

  const didFocus = useRef(false)

  /** Перечитать комнату. Фокус глубокой ссылки не трогаем, если явно не передан. */
  const refresh = async (focus?: number | null) => {
    if (!d) return
    try {
      const fresh = await api.getDialog(d.id)
      s.set(focus === undefined ? { activeDialog: fresh } : { activeDialog: fresh, focusMsgId: focus })
      if (fresh.me && fresh.messages.length) {
        const last = fresh.messages[fresh.messages.length - 1].id
        if (last > fresh.me.lastReadId) void api.patchMe(fresh.id, { lastReadId: last })
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setErr(t.dialogs.lockedPage)
    }
  }

  // отметить прочитанным при входе; прокрутка к фокусу/низу
  useEffect(() => {
    if (!d) return
    if (d.me && d.messages.length) {
      const last = d.messages[d.messages.length - 1].id
      if (last > d.me.lastReadId) void api.patchMe(d.id, { lastReadId: last })
    }
    // к фокусу глубокой ссылки прокручиваем один раз, дальше — к новым сообщениям
    const target = s.focusMsgId && !didFocus.current ? document.getElementById('m-' + s.focusMsgId) : null
    if (target) { target.scrollIntoView({ block: 'center' }); didFocus.current = true }
    else feedEnd.current?.scrollIntoView({ block: 'end' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.id, d?.messages.length])

  // мягкий опрос: ходы приходят из WhatsApp-переходов, свежесть важнее пушей
  useEffect(() => {
    if (!d?.me) return
    const iv = window.setInterval(() => void refresh(), 12_000)
    return () => window.clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.id, d?.me?.pid])

  if (!d) return <section className="screen"><p className="center-note">{t.dialogs.notFound}</p></section>

  const title = dialogTitle(d, t)
  const loc = locale === 'ru' ? 'ru-RU' : 'en-US'

  const ensureMember = async (): Promise<boolean> => {
    if (d.me) return true
    if (!d.joinable) { setErr(d.locked ? t.dialogs.lockedPage : t.dialogs.fullPage); return false }
    setAskName(true)
    return false
  }

  const join = async (name: string) => {
    setMyName(name)
    try {
      await api.joinDialog(d.id, name)
      setAskName(false)
      await refresh()
    } catch (e) {
      setAskName(false)
      setErr(e instanceof ApiError && e.code === 'locked' ? t.dialogs.lockedPage : t.errors.threadFailed)
    }
  }

  const sendText = async () => {
    const v = text.trim()
    if (!v || busy) return
    if (!(await ensureMember())) return
    setBusy(true)
    setErr('')
    try {
      const r = guessFor
        ? await api.sendMessage(d.id, { kind: 'guess', text: v, replyTo: guessFor.id })
        : await api.sendMessage(d.id, { kind: 'text', text: v, replyTo: s.pendingReplyTo })
      setText('')
      setGuessFor(null)
      s.set({ pendingReplyTo: null })
      setLastSent(r.messages[0])
      await refresh()
    } catch {
      setErr(t.errors.threadFailed)
    } finally {
      setBusy(false)
    }
  }

  const draw = async () => {
    if (!(await ensureMember())) return
    s.set({ mode: 'reply', replyDialogId: d.id, strokes: [], exStrokes: [], beforePNG: null, afterPNG: null, replySessionId: null })
    s.go('draw')
  }

  const toggleLock = async () => {
    await api.patchDialog(d.id, { locked: !d.locked }).catch(() => setErr(t.errors.threadFailed))
    await refresh()
  }

  const saveNames = async (p: { personal: string; shared: string; myName: string }) => {
    setMyName(p.myName)
    await api.patchMe(d.id, { personalTitle: p.personal || null, displayName: p.myName })
    if ((d.sharedTitle ?? '') !== p.shared) await api.patchDialog(d.id, { sharedTitle: p.shared || null })
    setRename(false)
    await refresh()
  }

  const copy = async () => {
    try { await navigator.clipboard.writeText(messageUrl(d.id)) } catch { /* нет буфера */ }
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  const quoted = (id: number | null) => (id ? d.messages.find((m) => m.id === id) ?? null : null)
  const openQuestions = d.messages.filter((m) => m.kind === 'drawing' && m.question && !isMine(m, d))
  const myGuessFor = (q: Message) => d.messages.some((m) => m.kind === 'guess' && m.replyTo === q.id && isMine(m, d))

  let lastDay = ''

  return (
    <section className="screen">
      <div className="room-head">
        <div>
          <div className="room-title" onClick={() => d.me && setRename(true)}>
            {title} {d.me && <span>✎</span>}
          </div>
          <div className="room-sub">
            {d.sharedTitle && d.sharedTitle !== title && <span>{d.sharedTitle} ·</span>}
            <span>{t.dialogs.participants(d.participants.length)}</span>
            {d.streak > 0 && <span className="streak-tag">· {t.dialogs.streak(d.streak)}</span>}
            {d.me && (
              <button className="lockbtn" onClick={toggleLock}>
                {d.locked ? `🔒 ${t.dialogs.locked}` : `🔓 ${t.dialogs.opened}`}
              </button>
            )}
          </div>
        </div>
      </div>

      {err && <div className="banner">{err}</div>}

      {!d.me && (
        <div className="guest">
          <p>{t.dialogs.preview}</p>
          {d.joinable && (
            <button className="btn btn-blue" style={{ padding: 12 }} onClick={() => setAskName(true)}>{t.dialogs.joinCta}</button>
          )}
        </div>
      )}

      <div className="feed">
        {d.messages.map((m) => {
          const day = new Date(m.createdAt).toLocaleDateString(loc, { day: 'numeric', month: 'long' })
          const showDay = day !== lastDay
          lastDay = day
          if (m.kind === 'system') {
            return (
              <div key={m.id} id={'m-' + m.id}>
                {showDay && <div className="day">{day}</div>}
                <div className="sys">{sysText(m, t)}</div>
              </div>
            )
          }
          const mine = isMine(m, d)
          const who = nameOf(m.from, d, t)
          const q = quoted(m.replyTo)
          const canGuess = m.kind === 'drawing' && !!m.question && !mine && !!d.me && !myGuessFor(m)
          return (
            <div key={m.id} id={'m-' + m.id}>
              {showDay && <div className="day">{day}</div>}
              <div className={'m' + (mine ? ' me' : '') + (m.kind === 'ai' ? ' ai' : '') + (s.focusMsgId === m.id ? ' focus' : '')}>
                <div className="who">{who.slice(0, 1) || '·'}</div>
                <div className="bub">
                  {!mine && m.kind !== 'ai' && <div className="name">{who}</div>}
                  {q && (
                    <div className="quote" onClick={() => document.getElementById('m-' + q.id)?.scrollIntoView({ block: 'center' })}>
                      {q.img && <img src={q.img} alt="" />}
                      {nameOf(q.from, d, t)}: {q.question ?? q.text}
                    </div>
                  )}
                  {m.img && <img src={m.img} alt="" />}
                  {m.text && <div style={{ marginTop: m.img ? 6 : 0 }}>{m.text}</div>}
                  {m.question && <span className="q">{m.question}</span>}
                  {d.me && m.kind !== 'ai' && (
                    <div className="acts">
                      {canGuess && <button onClick={() => { setGuessFor(m); s.set({ pendingReplyTo: null }) }}>{t.dialogs.guess}</button>}
                      {!canGuess && !mine && <button onClick={() => { setGuessFor(null); s.set({ pendingReplyTo: m.id }) }}>{t.dialogs.replyTo}</button>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
        <div ref={feedEnd} />
      </div>

      {lastSent && (
        <div className="nudge">
          <b>{t.dialogs.yourMove}</b>
          <button className="btn btn-blue" onClick={() => void shareMove(shareTextFor(lastSent, d, t), messageUrl(d.id, lastSent.id))}>
            {t.dialogs.shareMove}
          </button>
        </div>
      )}

      {d.me && (
        <div className="composer">
          {(guessFor || s.pendingReplyTo) && (
            <div className="quoting">
              <span>
                {guessFor
                  ? `${t.dialogs.guess}: ${guessFor.question}`
                  : `${t.dialogs.replyTo}: ${quoted(s.pendingReplyTo)?.question ?? quoted(s.pendingReplyTo)?.text ?? '✏️'}`}
              </span>
              <button onClick={() => { setGuessFor(null); s.set({ pendingReplyTo: null }) }}>×</button>
            </div>
          )}
          <div className="row">
            <button className="icon" onClick={draw} title={t.dialogs.composerDraw}>✏️</button>
            <input
              placeholder={guessFor ? t.dialogs.guessPlaceholder : t.dialogs.composerText}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendText()}
            />
            <button className="send" disabled={!text.trim() || busy} onClick={sendText}>→</button>
          </div>
          {openQuestions.length > 0 && !guessFor && openQuestions.some((q) => !myGuessFor(q)) && (
            <div className="chips" style={{ margin: '8px 0 0' }}>
              {openQuestions.filter((q) => !myGuessFor(q)).slice(-2).map((q) => (
                <button key={q.id} className="chip" onClick={() => setGuessFor(q)}>{t.dialogs.guess}: {q.question}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="btn-row col" style={{ marginTop: 14 }}>
        <button className="btn btn-ghost" onClick={copy}>{copied ? t.dialogs.copied : t.dialogs.copyLink}</button>
        {!d.me && (
          <button className="btn btn-ghost" onClick={() => { s.reset(); s.go('home') }}>{t.dialogs.goHome}</button>
        )}
      </div>

      {askName && <NameSheet initial={myName()} onDone={join} onClose={() => setAskName(false)} />}
      {rename && d.me && (
        <RenameSheet
          personal={d.me.personalTitle ?? ''}
          shared={d.sharedTitle ?? ''}
          myName={d.me.name}
          onSave={saveNames}
          onClose={() => setRename(false)}
        />
      )}
    </section>
  )
}

export type { Dialog }
